import {
  Box,
  chakra,
  Flex,
  Image as ChakraImage,
  Text,
  useCheckbox,
  UseCheckboxProps,
} from '@chakra-ui/react';

import { Image } from '../common';

interface RecipeListCategoryCheckboxProps extends UseCheckboxProps {
  icon?: string;
  label: string;
  count?: number;
  bgColor?: string;
  textColor?: string;
  type: 'icon' | 'simple' | 'favorite';
  inFavorite?: boolean;
}

export function RecipeListCategoryCheckbox(
  props: RecipeListCategoryCheckboxProps
) {
  const { type = 'icon' } = props;
  if (type === 'simple') return <RecipeCategorySimpleCheckbox {...props} />;
  if (type === 'favorite') return <RecipeCategoryFavoriteCheckbox {...props} />;
  return <RecipeCategoryIconCheckbox {...props} />;
}

function RecipeCategoryFavoriteCheckbox({
  label,
  icon,
  count = 0,
  bgColor,
  textColor,
  ...props
}: RecipeListCategoryCheckboxProps) {
  const { state, getCheckboxProps, getInputProps, getLabelProps, htmlProps } =
    useCheckbox(props);

  return (
    <chakra.label cursor="pointer" flex="none" {...htmlProps}>
      <input {...getInputProps()} hidden />
      <Flex
        alignItems="center"
        gap={{ base: '0.5', md: '1' }}
        backgroundColor={bgColor}
        color={textColor}
        rounded="full"
        py={{ base: '0.5', md: '1' }}
        px="3"
        border="1px solid"
        {...getCheckboxProps()}
        borderColor={state.isChecked ? 'black' : 'unset'}
      >
        <ChakraImage w="1rem" h="1rem" src={icon ?? ''} />
        <Box fontSize="md" textAlign="center" {...getLabelProps()}>
          <Text fontSize="xs" whiteSpace="nowrap">
            {`${label} (${count})`}
          </Text>
        </Box>
      </Flex>
    </chakra.label>
  );
}

function RecipeCategorySimpleCheckbox({
  label,
  ...props
}: RecipeListCategoryCheckboxProps) {
  const { state, getCheckboxProps, getInputProps, getLabelProps, htmlProps } =
    useCheckbox(props);

  return (
    <chakra.label cursor="pointer" {...htmlProps}>
      <input {...getInputProps()} hidden />
      <Box
        whiteSpace="nowrap"
        position="relative"
        rounded="lg"
        px="4"
        py="1"
        border="1px solid"
        {...getCheckboxProps()}
        bg={state.isChecked ? '#0000A310' : 'white'}
        borderColor={state.isChecked ? 'blue' : 'gray.75'}
        color={state.isChecked ? 'blue' : 'gray.75'}
      >
        <Box fontSize="md" textAlign="center" {...getLabelProps()}>
          {label}
        </Box>
      </Box>
    </chakra.label>
  );
}

function RecipeCategoryIconCheckbox({
  label,
  icon,
  ...props
}: RecipeListCategoryCheckboxProps) {
  const { state, getCheckboxProps, getInputProps, getLabelProps, htmlProps } =
    useCheckbox(props);

  return (
    <chakra.label
      display="flex"
      flexDir="column"
      alignItems="center"
      gap="2"
      cursor="pointer"
      {...htmlProps}
    >
      <input {...getInputProps()} hidden />
      <Box
        position="relative"
        bg="white"
        rounded="xl"
        w={{ base: '70px', md: '80px' }}
        h={{ base: '70px', md: '80px' }}
        p="3"
        border="2px solid"
        {...getCheckboxProps()}
        borderColor={state.isChecked ? '#006BD1' : 'transparent'}
      >
        {state.isChecked && (
          <Box position="absolute" right={0} top={-2}>
            <CheckIcon />
          </Box>
        )}

        {icon && (
          <Box position="relative" w="full" h="full">
            <Image src={icon} fill alt={label} />
          </Box>
        )}
      </Box>
      <Box
        fontSize={{ base: 'xs', md: 'sm' }}
        textAlign="center"
        {...getLabelProps()}
      >
        {label}
      </Box>
    </chakra.label>
  );
}

function CheckIcon() {
  return (
    <svg
      width="28"
      height="27"
      viewBox="0 0 28 27"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask
        id="mask0_16545_39600"
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="28"
        height="27"
      >
        <rect x="0.666992" width="26.6667" height="26.6667" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_16545_39600)">
        <rect x="9" y="6" width="10" height="10" fill="white" />
        <path
          d="M14.0007 23.4723C13.7969 23.4723 13.5886 23.4399 13.3757 23.3751C13.1627 23.3103 12.9636 23.2038 12.7784 23.0556L6.11176 17.9445C5.8525 17.7408 5.65806 17.5047 5.52843 17.2362C5.3988 16.9677 5.33398 16.676 5.33398 16.3612V3.33342C5.33398 2.77786 5.52843 2.30564 5.91732 1.91675C6.30621 1.52786 6.77843 1.33342 7.33398 1.33342H20.6673C21.2229 1.33342 21.6951 1.52786 22.084 1.91675C22.4729 2.30564 22.6673 2.77786 22.6673 3.33342V16.3612C22.6673 16.676 22.6025 16.9677 22.4729 17.2362C22.3432 17.5047 22.1488 17.7408 21.8895 17.9445L15.2229 23.0556C15.0377 23.2038 14.8386 23.3103 14.6257 23.3751C14.4127 23.4399 14.2044 23.4723 14.0007 23.4723ZM12.834 11.8334L11.1673 10.1667C10.9636 9.96305 10.7321 9.86119 10.4729 9.86119C10.2136 9.86119 9.98213 9.96305 9.77843 10.1667C9.57473 10.3705 9.47287 10.6066 9.47287 10.8751C9.47287 11.1436 9.57473 11.3797 9.77843 11.5834L12.1118 13.9445C12.3155 14.1482 12.5516 14.2501 12.8201 14.2501C13.0886 14.2501 13.3247 14.1482 13.5284 13.9445L18.2507 9.22231C18.4544 9.0186 18.5562 8.78712 18.5562 8.52786C18.5562 8.2686 18.4544 8.03712 18.2507 7.83342C18.0469 7.62971 17.8108 7.52786 17.5423 7.52786C17.2738 7.52786 17.0377 7.62971 16.834 7.83342L12.834 11.8334Z"
          fill="#006BD1"
        />
      </g>
    </svg>
  );
}
